import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import moment from 'moment';

import { LOYALTY_NUMBER_PREFIX } from 'constants/passengers';
import { passengerShape, passengersShape } from 'constants/propTypes';
import {
  isValidName,
  isValidLoyaltyNumber,
  isValidEmail,
  isValidCountryCode,
  isValidPhoneNumberWithCountryCode,
  isValidDate
} from 'utils/validators';
import PassengerType from 'components/ui/PassengerType';
import CoachAndSeatButton from 'components/shared/CoachAndSeatButton';
import Label from 'components/ui/Label';
import { customMealCode, mealTypesToDisplay } from 'types/meals';
import { Column, Row } from 'components/shared/Layout';
import EmailLanguage from 'components/ui/EmailLanguage';
import { passengerEntitledToMeal } from 'utils/meals';

import DiallingCodesSelector from '../../components/ui/DiallingCodesSelector';

import MealsSelect from './MealsSelect';
import {
  StyledInputWrapper,
  CenteredFlexWrapper,
  NoWrapFlexWrapper,
  StyledInput,
  StyledInputError,
  StyledLabel,
  StyledPhoneWrapper,
  StyledCountryWrapper,
  FormContainer,
  ApplyLabel,
  HeaderContainer,
  BodyContainer,
  FieldContainer,
  LoyaltyContainer,
  PhoneContainer,
  SubmitLoyaltyNumberButton,
  MealsContainer
} from './BookingPathPassengerDetails.styles';

const PassengerDetailsForm = ({
  selectedSeats,
  selectedPassenger,
  selectedPassengers,
  onSelectNewPassenger,
  onRemovePassengerSelection,
  register,
  fields,
  hasError = () => null,
  getError = () => null,
  showDOB,
  copyLastNameToAllPassengers,
  copyEmailAllPassengers,
  copyPhoneNumberAllPassengers,
  passengerDetails,
  getValues,
  trigger,
  onSubmitLoyaltyNumber,
  isDisabled,
  exchangePath = false,
  isHotelOnly = false,
  direction = 'outbound',
  meals = [],
  setMealsForPassenger = () => null,
  mealsToUpdate = {},
  bookingLanguage,
  setBookingLanguage,
  hasNoMealRoute = false
}) => {
  const listOfMeals = useMemo(
    () => meals.map(({ code, name }) => ({ id: code, name })).sort((a, b) => String(a.name).localeCompare(String(b.name))),
    [meals]
  );

  return (
    <>
      <Row span={14} start={1}>
        <Column span={2}>
          <Label htmlFor="bookingLanguage">
            <FormattedMessage id="groupDetails.language" />
          </Label>
          <div>
            <EmailLanguage initialValue={bookingLanguage} onChange={(event) => setBookingLanguage(event.value)} />
          </div>
        </Column>
      </Row>
      {fields.map((field, index) => {
        const passenger = passengerDetails.find((p) => p.uuid === field.uuid);

        const seatDetails = selectedSeats.find((paxseat) => paxseat.passenger.id === passenger.id);
        const showSelectMeal = !hasNoMealRoute && passengerEntitledToMeal(passenger, seatDetails);

        const isPassengerSeatSelected =
          (selectedPassenger && selectedPassenger.id === passenger.id) ||
          (selectedPassengers?.length && selectedPassengers.some(({ id }) => passenger.id === id));

        const handleCountryCodeChange = () => trigger(`passengers.${index}.phoneNumber`);

        const updatePassengerMeal = (e) => {
          const mealCode = e.target.value;
          const passengerId = passenger.id;

          setMealsForPassenger({ direction, mealCode, passengerId });
        };

        const isFirstNameError = hasError({
          name: 'firstName',
          index
        });
        const isLastNameError = hasError({
          name: 'lastName',
          index
        });
        const isBirthdateError = hasError({
          name: 'birthDate',
          index
        });
        const isLoyaltyNumberError = hasError({
          name: 'loyaltyNumber',
          index
        });

        const hasEmailError = hasError({
          name: 'email',
          index
        });
        const hasPhoneCountryCodeError = hasError({
          name: 'phoneCountryCode',
          index
        });
        const hasPhoneNumberError = hasError({
          name: 'phoneNumber',
          index
        });

        const loyaltyError = getError({
          name: 'loyaltyNumber',
          index
        });

        const firstNameError = getError({
          name: 'firstName',
          index
        });

        const lastNameError = getError({
          name: 'lastName',
          index
        });

        const loyaltyNumber = getValues()?.passengers?.[index]?.loyaltyNumber;

        const handleSubmitLoyaltyNumber = (e) => {
          e.preventDefault();
          onSubmitLoyaltyNumber(index, loyaltyNumber);
        };

        const handleCoachAndSeat = (e) => {
          e.preventDefault();

          if (isPassengerSeatSelected) {
            onRemovePassengerSelection(passenger.id);
          } else {
            onSelectNewPassenger(passenger.id);
          }
        };

        const isReadOnly = isDisabled || passenger.isReadOnly;
        const isPhoneDisabled = Boolean((isReadOnly && passenger.phoneNumber) || passenger.isPhoneNumberAndEmailDisabled);
        const isEmailDisabled = Boolean((isReadOnly && passenger.email) || passenger.isPhoneNumberAndEmailDisabled);

        return (
          <FormContainer key={field.id} isBackground={index % 2 === 0}>
            <HeaderContainer>
              <CenteredFlexWrapper>
                <StyledLabel marginRight="8px">
                  {index + 1}. <PassengerType type={passenger.type} disabilityType={passenger.disabilityType} passengerId={passenger.id} />
                </StyledLabel>
                {seatDetails && !isHotelOnly ? (
                  <>
                    <StyledLabel marginRight="8px">
                      {'- '}
                      <FormattedMessage id="paxForm.seatAndCoach" />
                    </StyledLabel>
                    <CoachAndSeatButton
                      seatDetails={seatDetails}
                      selected={isPassengerSeatSelected}
                      data-cy="passenger-button"
                      onClick={handleCoachAndSeat}
                    />
                  </>
                ) : null}
              </CenteredFlexWrapper>
              <LoyaltyContainer>
                <NoWrapFlexWrapper>
                  <StyledLabel marginRight="8px">
                    <FormattedMessage id="paxForm.loyaltyNumber" />
                  </StyledLabel>

                  {LOYALTY_NUMBER_PREFIX}

                  {isReadOnly && passenger.loyaltyNumber ? (
                    passenger.loyaltyNumber
                  ) : (
                    <StyledInput
                      data-lpignore="true"
                      type="number"
                      inputmode="numeric"
                      width="96px"
                      disabled={isReadOnly}
                      {...register(`passengers.${index}.loyaltyNumber`, {
                        validate: (value) => isValidLoyaltyNumber(value) || 'paxForm.validator.invalidLoyaltyNumber'
                      })}
                    />
                  )}
                  {!isReadOnly ? (
                    <SubmitLoyaltyNumberButton
                      disabled={loyaltyNumber?.length !== 8}
                      data-cy={`${index}-submit-loyaltyNumber-button`}
                      onClick={handleSubmitLoyaltyNumber}
                    >
                      <FormattedMessage id="paxForm.enter" />
                    </SubmitLoyaltyNumberButton>
                  ) : null}
                </NoWrapFlexWrapper>
                {isLoyaltyNumberError && (
                  <StyledInputError data-cy={`error-${index}-loyaltyNumber`}>
                    <FormattedMessage id={loyaltyError} />
                  </StyledInputError>
                )}
              </LoyaltyContainer>
            </HeaderContainer>
            <BodyContainer>
              <FieldContainer>
                <StyledLabel>
                  <FormattedMessage id="paxForm.firstName" />
                </StyledLabel>
                <StyledInputWrapper>
                  <StyledInput
                    data-lpignore="true"
                    type="text"
                    {...register(`passengers.${index}.firstName`, {
                      required: 'paxForm.validator.invalidFirstName',
                      validate: (value) => isValidName(value) || 'paxForm.validator.invalidFirstName'
                    })}
                    isInvalid={isFirstNameError}
                    disabled={isReadOnly || (isHotelOnly && passenger.firstName && index === 0)}
                  />
                  {isFirstNameError && (
                    <StyledInputError data-cy={`error-${index}-firstName`}>
                      <FormattedMessage id={firstNameError} />
                    </StyledInputError>
                  )}
                </StyledInputWrapper>
              </FieldContainer>
              <FieldContainer>
                <StyledLabel>
                  <FormattedMessage id="paxForm.lastName" />
                  {index === 0 && fields.length > 1 && !isReadOnly && copyLastNameToAllPassengers ? (
                    <ApplyLabel id="copy-last-name-btn" onClick={copyLastNameToAllPassengers}>
                      <FormattedMessage id="paxForm.applyToAll" />
                    </ApplyLabel>
                  ) : null}
                </StyledLabel>
                <StyledInputWrapper>
                  <StyledInput
                    data-lpignore="true"
                    type="text"
                    {...register(`passengers.${index}.lastName`, {
                      required: 'paxForm.validator.invalidLastName',
                      validate: (value) => isValidName(value) || 'paxForm.validator.invalidLastName'
                    })}
                    isInvalid={isLastNameError}
                    disabled={isReadOnly || (isHotelOnly && passenger.lastName && index === 0)}
                  />
                  {isLastNameError && (
                    <StyledInputError data-cy={`error-${index}-lastName`}>
                      <FormattedMessage id={lastNameError} />
                    </StyledInputError>
                  )}
                </StyledInputWrapper>
              </FieldContainer>
              {showDOB && (
                <FieldContainer>
                  <StyledLabel>
                    <FormattedMessage id="paxForm.birthDate" />
                  </StyledLabel>
                  <StyledInputWrapper>
                    <StyledInput
                      data-lpignore="true"
                      type="date"
                      {...register(`passengers.${index}.birthDate`, { required: true, validate: isValidDate })}
                      disabled={exchangePath ? field.birthDate !== null : isReadOnly}
                      max={moment().format('YYYY-MM-DD')}
                      min="1900-01-01"
                    />
                    {isBirthdateError && (
                      <StyledInputError data-cy={`error-${index}-birthDate`}>
                        <FormattedMessage id="paxForm.validator.invalidBirthDate" />
                      </StyledInputError>
                    )}
                  </StyledInputWrapper>
                </FieldContainer>
              )}
              <FieldContainer>
                <StyledLabel>
                  <FormattedMessage id="paxForm.email" />
                  {index === 0 && fields.length > 1 && !isReadOnly && copyEmailAllPassengers ? (
                    <ApplyLabel id="copy-email-btn" onClick={copyEmailAllPassengers}>
                      <FormattedMessage id="paxForm.applyToAll" />
                    </ApplyLabel>
                  ) : null}
                </StyledLabel>
                <StyledInputWrapper>
                  <StyledInput
                    data-lpignore="true"
                    type="text"
                    inputMode="email"
                    {...register(`passengers.${index}.email`, {
                      required: !isEmailDisabled,
                      validate: (value) => (!isEmailDisabled ? isValidEmail(value) : true)
                    })}
                    isInvalid={hasEmailError}
                    disabled={isEmailDisabled}
                  />
                  {hasEmailError && (
                    <StyledInputError data-cy={`error-${index}-email`}>
                      <FormattedMessage id="paxForm.validator.invalidEmail" />
                    </StyledInputError>
                  )}
                </StyledInputWrapper>
              </FieldContainer>
              <FieldContainer minWidth="400px">
                <PhoneContainer>
                  {!isPhoneDisabled && (
                    <StyledCountryWrapper>
                      <StyledLabel>
                        <FormattedMessage id="paxForm.countryCode" />
                      </StyledLabel>
                      <StyledInputWrapper>
                        <DiallingCodesSelector
                          required
                          validate={isValidCountryCode}
                          name={`passengers.${index}.phoneCountryCode`}
                          id={`passengers.${index}.phoneCountryCode`}
                          classes="booking-path-passenger-form-code-selector"
                          isInvalid={hasPhoneCountryCodeError}
                          disabled={isPhoneDisabled}
                          onChange={handleCountryCodeChange}
                        />
                      </StyledInputWrapper>
                    </StyledCountryWrapper>
                  )}
                  <StyledPhoneWrapper>
                    <StyledLabel>
                      <FormattedMessage id="paxForm.phoneNumber" />
                      {index === 0 && fields.length > 1 && !isPhoneDisabled && copyPhoneNumberAllPassengers ? (
                        <ApplyLabel id="copy-phone-number-btn" onClick={copyPhoneNumberAllPassengers}>
                          <FormattedMessage id="paxForm.applyToAll" />
                        </ApplyLabel>
                      ) : null}
                    </StyledLabel>
                    <StyledInputWrapper>
                      <StyledInput
                        data-lpignore="true"
                        type="number"
                        inputMode="tel"
                        {...register(`passengers.${index}.phoneNumber`, {
                          required: !isPhoneDisabled,
                          validate: (value) =>
                            !isPhoneDisabled
                              ? isValidPhoneNumberWithCountryCode(value, getValues(`passengers.${index}.phoneCountryCode`))
                              : true
                        })}
                        isInvalid={!isPhoneDisabled && hasPhoneNumberError}
                        disabled={isPhoneDisabled}
                      />
                    </StyledInputWrapper>
                  </StyledPhoneWrapper>
                </PhoneContainer>
                {(hasPhoneCountryCodeError || hasPhoneNumberError) && (
                  <StyledInputError data-cy={`error-${index}-phoneNumber`}>
                    <FormattedMessage id="paxForm.validator.invalidPhoneNumber" />
                  </StyledInputError>
                )}
              </FieldContainer>
            </BodyContainer>
            <MealsContainer>
              <MealsSelect
                name={`passengers.${index}.meal`}
                defaultValue={
                  mealsToUpdate?.[direction]?.find(({ passengerId }) => passengerId === passenger.id)?.mealCode ||
                  customMealCode.REGULAR_MEAL
                }
                meals={listOfMeals}
                onChange={updatePassengerMeal}
                placeholder={!showSelectMeal ? `mealTypes.${mealTypesToDisplay.NO_MEAL}` : ''}
                disabled={listOfMeals.length <= 1 || !showSelectMeal}
              />
            </MealsContainer>
          </FormContainer>
        );
      })}
    </>
  );
};

PassengerDetailsForm.propTypes = {
  selectedSeats: PropTypes.arrayOf(
    PropTypes.shape({
      passenger: PropTypes.shape({
        id: PropTypes.string
      }),
      number: PropTypes.string,
      coach: PropTypes.string
    })
  ),
  selectedPassenger: passengerShape,
  selectedPassengers: PropTypes.arrayOf(passengerShape),
  onSelectNewPassenger: PropTypes.func,
  onRemovePassengerSelection: PropTypes.func,
  register: PropTypes.func,
  fields: passengersShape,
  hasError: PropTypes.func,
  getError: PropTypes.func,
  showDOB: PropTypes.bool,
  copyLastNameToAllPassengers: PropTypes.func,
  copyEmailAllPassengers: PropTypes.func,
  copyPhoneNumberAllPassengers: PropTypes.func,
  passengerDetails: passengersShape,
  getValues: PropTypes.func,
  trigger: PropTypes.func,
  onSubmitLoyaltyNumber: PropTypes.func,
  isDisabled: PropTypes.bool,
  exchangePath: PropTypes.bool,
  isHotelOnly: PropTypes.bool,
  direction: PropTypes.string,
  meals: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string,
      name: PropTypes.string
    })
  ),
  mealsToUpdate: PropTypes.shape({
    outbound: PropTypes.arrayOf(
      PropTypes.shape({
        passengerId: PropTypes.string,
        mealCode: PropTypes.string
      })
    ),
    inbound: PropTypes.arrayOf(
      PropTypes.shape({
        passengerId: PropTypes.string,
        mealCode: PropTypes.string
      })
    )
  }),
  setMealsForPassenger: PropTypes.func,
  bookingLanguage: PropTypes.string,
  setBookingLanguage: PropTypes.func,
  hasNoMealRoute: PropTypes.bool
};

export default PassengerDetailsForm;
